/**
 * Self status surface: separate helpful/harmful rows, authoritative duration,
 * stack presentation, join reconstruction, and rich mouse/focus tooltips.
 */

import React, { useEffect, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { useRenderCount } from '../../diagnostics/useRenderCount';
import { SPACE, RADII, FONT_SIZE, FONT_WEIGHT, INK } from '../theme';
import { Tooltip } from '../components/Tooltip';
import {
  createStatusTooltip,
  resolveStatusPresentation,
  type StatusDisposition,
} from '../status/StatusPresentation';
import './buffbar.css';

interface ActiveBuffEntry {
  effectId: string;
  abilityId: string;
  name: string;
  icon: string;
  color: number;
  expiresAt: number;
  duration: number;
  stacks: number;
  maxStacks: number;
  sourceId?: string;
  data?: Record<string, unknown> | null;
}

interface BuffBarProps {
  playerId: string | undefined;
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: SPACE.sm,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: SPACE.xs,
  justifyContent: 'center',
  minHeight: 34,
};

const iconBaseStyle: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: RADII.sm * 2,
  position: 'relative',
  background: `linear-gradient(180deg, ${INK.well}, ${INK.wellLo})`,
  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'help',
};

const iconContentStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.xl,
  lineHeight: 1,
  zIndex: 1,
};

const iconImgStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  objectFit: 'contain',
};

const ringBaseStyle: React.CSSProperties = {
  position: 'absolute',
  inset: -1,
  borderRadius: RADII.sm * 2,
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: 'transparent',
  opacity: 0.8,
  pointerEvents: 'none',
};

const timerStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: -2,
  right: -2,
  fontSize: 9,
  fontWeight: FONT_WEIGHT.bold,
  color: '#fff',
  background: 'rgba(0,0,0,0.7)',
  borderRadius: RADII.sm,
  padding: '0 2px',
  lineHeight: 1.2,
  zIndex: 2,
};

const stackStyle: React.CSSProperties = {
  position: 'absolute',
  top: -3,
  left: -3,
  minWidth: 14,
  height: 14,
  borderRadius: 7,
  padding: '0 3px',
  background: '#111827',
  border: '1px solid rgba(255,255,255,0.65)',
  color: '#fff',
  fontSize: 9,
  fontWeight: FONT_WEIGHT.bold,
  lineHeight: '12px',
  textAlign: 'center',
  zIndex: 3,
};

export const BuffBar: React.FC<BuffBarProps> = ({ playerId }) => {
  useRenderCount('BuffBar');
  const [buffs, setBuffs] = useState<Map<string, ActiveBuffEntry>>(new Map());
  const [, setTick] = useState(0);

  // Four hertz is enough for whole-second labels and the quarter ring. It
  // also prunes reconstructed entries even if a wire removal was missed.
  useEffect(() => {
    if (buffs.size === 0) return;
    const id = setInterval(() => {
      const now = Date.now();
      setBuffs(prev => {
        let changed = false;
        const next = new Map(prev);
        for (const [effectId, buff] of next) {
          if (buff.duration > 0 && buff.expiresAt <= now) {
            next.delete(effectId);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
      setTick(t => t + 1);
    }, 250);
    return () => clearInterval(id);
  }, [buffs.size]);

  useEffect(() => {
    const eventBus = EventBus.getInstance();
    const getRoom = () => (globalThis as any).nm?.getRoom?.();
    const reconstructedEffects = new Set<string>();
    let reconstructionProbe: ReturnType<typeof setTimeout> | null = null;
    let reconstructionAttempts = 0;
    const isSelf = (targetId: string) =>
      targetId === playerId || targetId === getRoom()?.sessionId;

    const upsert = (data: {
      effectId: string;
      duration?: number;
      color?: number;
      stacks?: number;
      maxStacks?: number;
      sourceId?: string;
      data?: Record<string, unknown> | null;
    }) => {
      setBuffs(prev => {
        const next = new Map(prev);
        const existing = next.get(data.effectId);
        const display = resolveStatusDisplay(data.effectId, data.data);
        const duration = data.duration ?? existing?.duration ?? 0;
        next.set(data.effectId, {
          effectId: data.effectId,
          abilityId: display.abilityId,
          name: display.name,
          icon: display.icon,
          color: data.color ?? display.color,
          expiresAt: duration > 0 ? Date.now() + duration : 0,
          duration,
          stacks: data.stacks ?? existing?.stacks ?? 1,
          maxStacks: data.maxStacks ?? existing?.maxStacks ?? 1,
          sourceId: data.sourceId ?? existing?.sourceId,
          data: data.data ?? existing?.data,
        });
        return next;
      });
    };

    const remove = (effectId: string) => {
      setBuffs(prev => {
        if (!prev.has(effectId)) return prev;
        const next = new Map(prev);
        next.delete(effectId);
        return next;
      });
    };

    const unsub = eventBus.on('vfx:status_effect', (data: {
      targetId: string;
      effectId: string;
      active: boolean;
      color?: number;
      duration?: number;
      sourceId?: string;
      data?: Record<string, unknown> | null;
    }) => {
      if (!isSelf(data.targetId)) return;
      if (data.active) upsert(data);
      else remove(data.effectId);
    });

    const unsubStacks = eventBus.on('vfx:status_stack_update', (data: {
      targetId: string;
      effectId: string;
      duration?: number;
      stacks?: number;
      maxStacks?: number;
      sourceId?: string;
      data?: Record<string, unknown> | null;
    }) => {
      if (isSelf(data.targetId)) upsert(data);
    });

    const unsubWireRemove = eventBus.on(EVENTS.WIRE_STATUS_REMOVED, (data: {
      targetId: string;
      effectId: string;
    }) => {
      if (isSelf(data.targetId)) remove(data.effectId);
    });

    const reconstructSelfAuras = () => {
      // Join restoration can precede React/VFX subscriptions, while the HUD
      // itself can mount before NetworkManager owns a room. Read the room
      // afresh both now and on net:connected so neither ordering loses buffs.
      const room = getRoom();
      const schemaPlayer = room?.state?.players?.get?.(room?.sessionId);
      schemaPlayer?.activeAuras?.forEach?.((aura: any, effectId: string) => {
        const remaining = Number(aura?.expiresAt) - Date.now();
        const resolvedEffectId = aura?.effectId || effectId;
        if (remaining <= 0 || reconstructedEffects.has(resolvedEffectId)) return;
        reconstructedEffects.add(resolvedEffectId);
        upsert({
          effectId: resolvedEffectId,
          duration: remaining,
          data: {
            source: 'consumable',
            definitionId: aura?.definitionId || resolvedEffectId,
            itemId: aura?.itemId || '',
          },
        });
      });
    };
    const probeForRestoredAuras = () => {
      reconstructSelfAuras();
      reconstructionAttempts += 1;
      if (reconstructionAttempts < 25) {
        reconstructionProbe = setTimeout(probeForRestoredAuras, 200);
      }
    };
    const restartRestorationProbe = () => {
      if (reconstructionProbe) clearTimeout(reconstructionProbe);
      reconstructionAttempts = 0;
      reconstructedEffects.clear();
      probeForRestoredAuras();
    };
    restartRestorationProbe();
    const unsubConnected = eventBus.on(EVENTS.NET_CONNECTED, restartRestorationProbe);

    return () => {
      if (reconstructionProbe) clearTimeout(reconstructionProbe);
      unsub();
      unsubStacks();
      unsubWireRemove();
      unsubConnected();
    };
  }, [playerId]);

  if (buffs.size === 0) return null;
  const now = Date.now();
  const entries = Array.from(buffs.values());
  const byDisposition = (disposition: StatusDisposition) => entries.filter(buff =>
    resolveStatusPresentation(buff.effectId, { data: buff.data }).disposition === disposition
  );

  const renderRow = (
    rowBuffs: ActiveBuffEntry[],
    disposition: StatusDisposition,
  ) => rowBuffs.length > 0 ? (
    <div
      aria-label={
        disposition === 'helpful'
          ? 'Helpful effects'
          : disposition === 'harmful'
            ? 'Harmful effects'
            : 'Other effects'
      }
      data-status-disposition={disposition}
      style={rowStyle}
    >
      {rowBuffs.map(buff => {
        const remaining = buff.duration > 0 ? Math.max(0, buff.expiresAt - now) : 0;
        const fraction = buff.duration > 0 ? remaining / buff.duration : 0;
        const timerLabel = formatRemainingDuration(remaining);
        const colorHex = `#${buff.color.toString(16).padStart(6, '0')}`;
        const presentation = resolveStatusPresentation(buff.effectId, {
          data: buff.data,
          name: buff.name,
          icon: buff.icon,
          color: buff.color,
          sourceName: resolveSourceName(buff.sourceId),
        });
        const iconStyle: React.CSSProperties = {
          ...iconBaseStyle,
          border: `1.5px solid ${colorHex}aa`,
          boxShadow: `0 0 8px ${colorHex}55, inset 0 0 6px rgba(0,0,0,0.5)`,
        };
        const ringStyle: React.CSSProperties = {
          ...ringBaseStyle,
          borderTopColor: colorHex,
          borderRightColor: fraction > 0.25 ? colorHex : 'transparent',
          borderBottomColor: fraction > 0.5 ? colorHex : 'transparent',
          borderLeftColor: fraction > 0.75 ? colorHex : 'transparent',
        };

        return (
          <Tooltip
            key={buff.effectId}
            data={createStatusTooltip(presentation, remaining, buff.stacks)}
            position="bottom"
          >
            <div
              className={`helix-buff-icon helix-status-${presentation.disposition}`}
              aria-label={`${presentation.name}, ${formatExactAriaDuration(remaining)}`}
              data-effect-id={buff.effectId}
              data-status-disposition={presentation.disposition}
              tabIndex={0}
              style={iconStyle}
            >
              <div style={iconContentStyle}>
                {buff.icon?.startsWith('/') ? (
                  <img src={buff.icon} alt={buff.name} style={iconImgStyle} />
                ) : (
                  buff.icon || buff.name.charAt(0).toUpperCase()
                )}
              </div>
              <div style={ringStyle} />
              {buff.stacks > 1 && (
                <div aria-label={`${buff.stacks} stacks`} style={stackStyle}>{buff.stacks}</div>
              )}
              {timerLabel && <div style={timerStyle}>{timerLabel}</div>}
            </div>
          </Tooltip>
        );
      })}
    </div>
  ) : null;

  return (
    <div style={containerStyle}>
      {renderRow(byDisposition('helpful'), 'helpful')}
      {renderRow(byDisposition('harmful'), 'harmful')}
      {renderRow(byDisposition('neutral'), 'neutral')}
    </div>
  );
};

function resolveStatusDisplay(
  effectId: string,
  data?: Record<string, unknown> | null,
): { abilityId: string; name: string; icon: string; color: number } {
  const match = abilityRegistry.getAll().find(a => a.vfx?.cast === effectId);
  const presentation = resolveStatusPresentation(effectId, {
    data,
    name: match?.name,
    icon: match?.icon,
  });
  return {
    abilityId: match?.id || effectId,
    name: presentation.name,
    icon: presentation.icon,
    color: presentation.color,
  };
}

function resolveSourceName(sourceId: string | undefined): string | undefined {
  if (!sourceId) return undefined;
  const room = (globalThis as any).nm?.getRoom?.();
  return room?.state?.players?.get?.(sourceId)?.heroName;
}

export function formatRemainingDuration(remainingMs: number): string {
  if (remainingMs <= 0) return '';
  const seconds = Math.ceil(remainingMs / 1000);
  if (seconds >= 60) return `${Math.ceil(seconds / 60)}m`;
  return `${seconds}s`;
}

function formatExactAriaDuration(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return `${seconds} seconds remaining`;
}
